import { EVENTS } from "./events";

/**
 * Records the NEW state of form controls — the part autocapture can't see (it reads the
 * element at click time, before the UI updates):
 *
 *   switch    -> control_toggled   { control, state: "on" | "off" }
 *   checkbox  -> checkbox_changed  { control, checked }
 *   radio     -> radio_selected    { control }
 *   text/textarea/native select -> field_changed
 *       { control, input_type, is_empty, value_length, value? }
 *
 * Text values are PRIVATE BY DEFAULT: `value` is sent only for fields inside an element
 * carrying data-ph-capture-value. Never sent, even with the attribute: password / hidden /
 * file inputs, cc-* autocomplete fields. Controls inside data-ph-mask, data-ph-block,
 * .ph-no-capture or [data-ph-no-capture] are skipped entirely (no event at all).
 *
 * A control's name comes from, in order: data-ph-name, aria-label, its <label>, an Ant
 * Design form-item label, name, id, placeholder. Add data-ph-name for a stable name.
 */

const MAX_NAME = 80;
const MAX_VALUE = 200;
const OPT_OUT = "[data-ph-mask],[data-ph-block],.ph-no-capture,[data-ph-no-capture]";
const OPT_IN_VALUE = "[data-ph-capture-value]";

// Input types that are never reported as text fields.
const SKIP_TYPES = new Set([
  "password", "hidden", "file", "submit", "button", "reset", "image",
  "range", "color", "checkbox", "radio",
]);

const clip = (s, n) => String(s == null ? "" : s).replace(/\s+/g, " ").trim().slice(0, n);

function labelText(el) {
  if (el.id) {
    const l = document.querySelector('label[for="' + (window.CSS && CSS.escape ? CSS.escape(el.id) : el.id) + '"]');
    if (l) return l.textContent;
  }
  const wrap = el.closest("label");
  if (wrap) return wrap.textContent;
  const item = el.closest(".ant-form-item");
  const itemLabel = item && item.querySelector(".ant-form-item-label label");
  return itemLabel ? itemLabel.textContent : "";
}

function controlName(el) {
  const named = el.closest("[data-ph-name]");
  return clip(
    (named && named.getAttribute("data-ph-name")) ||
      el.getAttribute("aria-label") ||
      labelText(el) ||
      el.getAttribute("name") ||
      el.id ||
      el.getAttribute("placeholder") ||
      "",
    MAX_NAME
  );
}

const optedOut = (el) => !!el.closest(OPT_OUT);

export function initControlTracking(capture) {
  const send = (event, props) => {
    try {
      capture(event, props);
    } catch (e) {
      // analytics must never break the app
    }
  };

  // --- native change: checkbox / radio / text / textarea / select ---------------------
  document.addEventListener(
    "change",
    (e) => {
      try {
        const el = e.target;
        if (!el || !el.tagName || optedOut(el)) return;
        const tag = el.tagName.toLowerCase();
        const type = (el.getAttribute("type") || "text").toLowerCase();

        if (tag === "input" && type === "checkbox") {
          send(EVENTS.CHECKBOX_CHANGED, { control: controlName(el), checked: !!el.checked });
          return;
        }
        if (tag === "input" && type === "radio") {
          if (el.checked) send(EVENTS.RADIO_SELECTED, { control: controlName(el) });
          return;
        }

        const isField = tag === "textarea" || tag === "select" || (tag === "input" && !SKIP_TYPES.has(type));
        if (!isField) return;
        if (el.closest(".ant-select")) return; // antd Select's inner search box
        const ac = (el.getAttribute("autocomplete") || "").toLowerCase();
        if (ac.indexOf("cc-") === 0 || ac.indexOf("password") !== -1) return;

        const value = tag === "select" ? (el.selectedOptions && el.selectedOptions[0] ? el.selectedOptions[0].text : "") : el.value;
        const props = {
          control: controlName(el),
          input_type: tag === "input" ? type : tag,
          is_empty: String(value || "").length === 0,
          value_length: String(value || "").length,
        };
        if (el.closest(OPT_IN_VALUE)) props.value = clip(value, MAX_VALUE);
        send(EVENTS.FIELD_CHANGED, props);
      } catch (err) {
        // ignore
      }
    },
    true
  );

  // --- switch: antd Switch is a <button role="switch">, it emits no DOM change event ----
  // Read aria-checked before the click and shortly after; report only if it really flipped
  // (a controlled switch whose parent rejects the change stays put and is not reported).
  document.addEventListener(
    "click",
    (e) => {
      try {
        const el = e.target && e.target.closest ? e.target.closest("[role=switch]") : null;
        if (!el || el.disabled || optedOut(el)) return;
        const before = el.getAttribute("aria-checked");
        setTimeout(() => {
          const after = el.getAttribute("aria-checked");
          if (after === before || (after !== "true" && after !== "false")) return;
          send(EVENTS.CONTROL_TOGGLED, {
            control: controlName(el),
            state: after === "true" ? "on" : "off",
          });
        }, 80);
      } catch (err) {
        // ignore
      }
    },
    true
  );
}
